import { Button } from './Button';
import { downloadCSV, toCSV, type CsvColumn } from '@/lib/csv';

type Props<T> = {
  rows: T[];
  columns: CsvColumn<T>[];
  filenamePrefix: string;
  disabled?: boolean;
  label?: string;
};

/**
 * Generic CSV export button. Each page that loads tabular data passes
 * its own columns descriptor — the rest is plumbing.
 *
 * Until the backend ships a proper /v1/exports endpoint, this stays
 * client-side: limited to the currently loaded page (50–100 rows for
 * tables, capped at 10 000 by toCSV).
 */
export function ExportButton<T>({ rows, columns, filenamePrefix, disabled, label = 'Exporter' }: Props<T>) {
  function go() {
    const { csv, truncated } = toCSV(rows, columns);
    const filename = `${filenamePrefix}_${stamp()}.csv`;
    downloadCSV(filename, csv);
    if (truncated) {
      // Soft warning: the operator wanted a full dump, we cut the file
      // at the safe ceiling. The platform-side export will arrive in a
      // future sprint.
      alert(
        `Export limité aux 10 000 premières lignes pour des raisons de performance. ` +
          `Demandez un export complet à la plateforme une fois /v1/exports disponible.`,
      );
    }
  }

  return (
    <Button icon="export" onClick={go} disabled={disabled || rows.length === 0}>
      {label}
    </Button>
  );
}

function stamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}
